import { KeyboardEvent, MouseEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { classNames } from 'primereact/utils';
import { EnterKeyIcon } from '../../../atoms/icons';
import { SuggestionItem } from './BundleSectionsTreeNodeTemplate';

interface SectionRenameInputProps {
  initialValue?: string;
  placeholder?: string;
  suggestions?: SuggestionItem[];
  onCommit: (value: string | SuggestionItem) => void;
  onCancel: () => void;
  onChange?: (value: string) => void;
}

/**
 * The inline rename input from the sections tree, extracted so other dialogs (e.g.
 * MoveSectionsDialog) can reuse it: plain suggestion data and callbacks, no react-arborist.
 */
export const SectionRenameInput: React.FC<SectionRenameInputProps> = ({
  initialValue = '',
  placeholder = 'Enter category name',
  suggestions,
  onCommit,
  onCancel,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [text, setText] = useState<string>(initialValue);
  const [dropdownRect, setDropdownRect] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  } | null>(null);
  // -1 = nothing highlighted until an arrow key is pressed.
  const [highlightIndex, setHighlightIndex] = useState<number>(-1);

  const filteredSuggestions = useMemo(() => {
    const list = suggestions ?? [];
    const q = text.trim().toLowerCase();
    const base = q.length ? list.filter((item) => item.name.toLowerCase().includes(q)) : list;
    return base.slice(0, 6);
  }, [suggestions, text]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    setHighlightIndex((prev) => {
      if (prev < 0) return -1;
      if (!filteredSuggestions.length) return -1;
      return Math.min(prev, filteredSuggestions.length - 1);
    });
  }, [filteredSuggestions]);

  // Positions the portalled dropdown against the input, recomputing on scroll/resize so it
  // stays glued to it — this is what stops MoveSectionsDialog's scroll container clipping it.
  useLayoutEffect(() => {
    if (!filteredSuggestions.length) {
      setDropdownRect(null);
      return;
    }
    const update = () => {
      const el = wrapperRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Flip above the input when there's no room below (dropdown max height + a margin).
      const preferredHeight = 232;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
      setDropdownRect(
        flipUp
          ? { bottom: window.innerHeight - rect.top + 4, left: rect.left, width: rect.width }
          : { top: rect.bottom + 4, left: rect.left, width: rect.width },
      );
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [filteredSuggestions]);

  const commit = (finalValue: string | SuggestionItem) => {
    const trimmed = typeof finalValue === 'string' ? finalValue.trim() : finalValue.name.trim();
    if (!trimmed) return;
    onCommit(finalValue);
  };

  const handleTextChange = (next: string) => {
    setText(next);
    onChange?.(next);
    // Typing clears the highlight — Enter submits typed text until the
    // user uses an arrow key to explicitly pick a suggestion.
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredSuggestions.length) {
        setHighlightIndex((i) => (i < 0 ? 0 : (i + 1) % filteredSuggestions.length));
      }
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredSuggestions.length) {
        setHighlightIndex((i) =>
          i < 0 ? filteredSuggestions.length - 1 : (i - 1 + filteredSuggestions.length) % filteredSuggestions.length,
        );
      }
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const highlighted = highlightIndex >= 0 ? filteredSuggestions[highlightIndex] : undefined;
      commit(highlighted ?? text);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
      return;
    }
  };

  const handleSuggestionMouseDown = (e: MouseEvent, item: SuggestionItem) => {
    // Fire on mousedown (before the input's blur/mouseup) so the click
    // isn't cancelled by the blur handler.
    e.preventDefault();
    e.stopPropagation();
    commit(item);
  };

  const isSubmitDisabled = !text.trim().length;

  return (
    <div className="m-0 w-full pr-2">
      <div ref={wrapperRef} className="relative flex w-full items-center">
        <input
          ref={inputRef}
          type="text"
          value={text}
          onChange={(e) => handleTextChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            e.stopPropagation();
            setTimeout(() => {
              if (document.activeElement !== inputRef.current) {
                onCancel();
              }
            }, 150);
          }}
          placeholder={placeholder}
          className={classNames(
            'm-0 w-full font-sans text-[0.875rem] font-normal text-black placeholder:text-[0.875rem]',
            'appearance-none rounded-[4px] bg-white transition-colors duration-200',
            'border-fletchers-green-500 border focus:border-black',
            'outline-none focus:outline-none',
            'shadow-none focus:shadow-none',
            'py-1 pr-11 pl-2',
          )}
        />
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => commit(text)}
          disabled={isSubmitDisabled}
          aria-label="Save"
          className={classNames(
            'absolute inset-y-0 right-2 my-auto flex h-4 w-4 items-center justify-center rounded-[3px] border',
            'bg-fletchers-gray-60 border-fletchers-gray-115 text-fletchers-gray-300',
            'hover:!bg-fletchers-gray-105 disabled:cursor-not-allowed disabled:opacity-40',
            !isSubmitDisabled && 'cursor-pointer',
          )}
        >
          <EnterKeyIcon />
        </button>
        {filteredSuggestions.length > 0 &&
          dropdownRect &&
          createPortal(
            <ul
              style={{
                position: 'fixed',
                top: dropdownRect.top,
                bottom: dropdownRect.bottom,
                left: dropdownRect.left,
                width: dropdownRect.width,
              }}
              className={classNames(
                'z-[1200] max-h-56 overflow-y-auto',
                'border-fletchers-gray-110 rounded-[4px] border bg-white shadow-md',
                'py-1',
              )}
            >
              {filteredSuggestions.map((item, i) => (
                <li
                  key={item.id}
                  onMouseDown={(e) => handleSuggestionMouseDown(e, item)}
                  onMouseEnter={() => setHighlightIndex(i)}
                  className={classNames(
                    'cursor-pointer px-2 py-1 text-[0.875rem]',
                    i === highlightIndex ? 'bg-fletchers-green-50' : 'hover:bg-fletchers-green-50',
                  )}
                >
                  {item.name}
                </li>
              ))}
            </ul>,
            document.body,
          )}
      </div>
    </div>
  );
};
